import { useEffect, useState } from 'react';
import { RECORD_RIGHT_LABEL, ROLE_LABEL, seesEveryContact, type Role } from '@ehr/shared';
import { apiErrorText } from '../../lib/api.js';
import { askConfirm } from '../../lib/dialogs.js';
import { clockTime } from '../../lib/timePrefs.js';
import {
  fetchWhoHasAccess,
  removeRoleAccess,
  removeUserAccess,
  type RoleGrantRow,
  type UserGrantRow,
  type WhoHasAccess,
} from '../../lib/contactAccess.js';
import { SaModal, initials, saMessage, type SaMessage } from './SaModal.js';

/** V11 fmtDateTime: 10-30-2026 9:05 AM (or 09:05 on a 24-hour profile). */
export function whenText(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}-${p(d.getDate())}-${d.getFullYear()} ${clockTime(d)}`;
}

/**
 * ehr#730 (SHARE_ACCESS_10-30-V11 Manage Access, "Who has access"): the owner (Change owner), each
 * person's and role's grant (Change, Make Owner, Remove) and the History. Open to the owner, admins,
 * managers and anyone given Share with others on the contact (the server checks).
 */
export function ManageAccessModal({
  contactId,
  onClose,
  onShare,
  onChangeUser,
  onChangeRole,
  onReassign,
  onChanged,
}: {
  contactId: string;
  onClose: () => void;
  onShare: () => void;
  onChangeUser: (row: UserGrantRow) => void;
  onChangeRole: (row: RoleGrantRow) => void;
  /** Change owner / Make Owner, with the owner as Who has access read it. */
  onReassign: (presetUserId: string | undefined, ownerUserId: string | null) => void;
  onChanged?: () => void;
}) {
  const [data, setData] = useState<WhoHasAccess | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<SaMessage | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let live = true;
    fetchWhoHasAccess(contactId)
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(apiErrorText(e, 'Could not load who has access.')));
    return () => {
      live = false;
    };
  }, [contactId, tick]);

  const remove = async (label: string, run: () => Promise<unknown>) => {
    const ok = await askConfirm({
      message: `Remove access for ${label} on ${data?.contact.name ?? 'this contact'}?`,
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    try {
      await run();
      setMsg(saMessage('Access removed'));
      setTick((t) => t + 1);
      onChanged?.();
    } catch (e) {
      setMsg(saMessage(apiErrorText(e, 'Could not remove access.'), true));
    }
  };

  const roleName = (r: string) => ROLE_LABEL[r as Role] ?? r;
  const canReassign = !!data?.mine.canReassign;

  return (
    <SaModal
      title="Who has access"
      sub={data?.contact.name}
      onClose={onClose}
      msg={msg}
      onMsgDone={() => setMsg(null)}
      foot={{
        right: (
          <>
            <button type="button" className="btn" onClick={onClose}>
              Close
            </button>
            <button type="button" className="btn primary" onClick={onShare} disabled={!data}>
              Share
            </button>
          </>
        ),
      }}
    >
      {error ? (
        <p className="acs-muted" role="alert">
          {error}
        </p>
      ) : !data ? (
        <p className="acs-muted">Loading…</p>
      ) : (
        <>
          <table className="acs-table list" id="acs-access-table">
            <thead>
              <tr>
                <th>Name</th>
                <th className="acs-hide-m">Role</th>
                <th>Access</th>
                <th className="acs-hide-m">Record permissions</th>
                <th className="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr data-row="owner">
                <td>
                  <div className="acs-flex">
                    <span className="acs-avatar sm">{initials(data.contact.ownerName ?? 'U')}</span>
                    <div>
                      <div className="acs-nm">{data.contact.ownerName ?? 'Unassigned'}</div>
                      <span className="acs-pill">Owner</span>
                    </div>
                  </div>
                </td>
                <td className="acs-hide-m">
                  {data.contact.ownerRole ? roleName(data.contact.ownerRole) : ''}
                </td>
                <td>{data.contact.ownerUserId ? 'Everything' : ''}</td>
                {/* the owner shares; Reassign and Archive stay with admins and managers */}
                <td className="acs-hide-m">
                  {!data.contact.ownerUserId
                    ? ''
                    : seesEveryContact(data.contact.ownerRole)
                      ? 'All'
                      : RECORD_RIGHT_LABEL.share}
                </td>
                <td className="right">
                  {canReassign && (
                    <button
                      type="button"
                      className="btn sm"
                      onClick={() => onReassign(undefined, data.contact.ownerUserId)}
                    >
                      Change owner
                    </button>
                  )}
                </td>
              </tr>
              {data.users.map((u) => (
                <tr key={u.userId} data-row={u.userId}>
                  <td>
                    <div className="acs-flex">
                      <span className="acs-avatar sm">{initials(u.name)}</span>
                      <span className="acs-nm">{u.name}</span>
                    </div>
                  </td>
                  <td className="acs-hide-m">{roleName(u.role)}</td>
                  <td>{u.summary}</td>
                  <td className="acs-hide-m">{u.rightsSummary}</td>
                  <td className="right">
                    <div className="acs-btn-row">
                      <button type="button" className="btn sm" onClick={() => onChangeUser(u)}>
                        Change
                      </button>
                      {canReassign && (
                        <button
                          type="button"
                          className="btn sm"
                          onClick={() => onReassign(u.userId, data.contact.ownerUserId)}
                        >
                          Make Owner
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn sm danger"
                        onClick={() =>
                          void remove(u.name, () => removeUserAccess(contactId, u.userId))
                        }
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {data.roles.map((r) => (
                <tr key={r.role} data-row={r.role}>
                  <td>
                    <div className="acs-nm">{r.label}</div>
                    <div className="acs-chips" style={{ marginTop: 6 }}>
                      <span className="acs-tag">Role</span>
                      <span className="acs-tag">
                        {r.members.length - r.excluded.length} of {r.members.length} members
                      </span>
                      {r.excluded.length > 0 && (
                        <span className="acs-tag">
                          Not included: {r.excluded.map((x) => x.name).join(', ')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="acs-hide-m">Role</td>
                  <td>{r.summary}</td>
                  <td className="acs-hide-m">{r.rightsSummary}</td>
                  <td className="right">
                    <div className="acs-btn-row">
                      <button type="button" className="btn sm" onClick={() => onChangeRole(r)}>
                        Change
                      </button>
                      <button
                        type="button"
                        className="btn sm danger"
                        onClick={() =>
                          void remove(`role ${r.label}`, () => removeRoleAccess(contactId, r.role))
                        }
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="acs-label acs-gap" style={{ marginBottom: 6 }}>
            History
          </div>
          <table className="acs-table list" id="acs-history-table">
            <thead>
              <tr>
                <th>When</th>
                <th className="acs-hide-m">By</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.history.length ? (
                data.history.map((h, i) => (
                  <tr key={`${h.at}${i}`}>
                    <td style={{ whiteSpace: 'nowrap' }}>{whenText(h.at)}</td>
                    <td className="acs-hide-m">{h.by}</td>
                    <td>{h.action}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="acs-empty">
                    No changes yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </SaModal>
  );
}
